"use client"

/* eslint-disable functional/immutable-data -- ファイル選択と非同期処理の世代をrefで管理する */
import Image from "next/image"
import { type ChangeEvent, useEffect, useRef, useState } from "react"

import {
  type BrickReferenceImage,
  prepareBrickReferenceImage,
} from "@/lib/brick-reference-image"

import styles from "./BrickReferenceImageInput.module.css"

type Props = {
  value: BrickReferenceImage | null
  onChange: (value: BrickReferenceImage | null) => void
  disabled?: boolean
  error?: string
  id?: string
  onBusyChange?: (busy: boolean) => void
}

export function BrickReferenceImageInput({
  value,
  onChange,
  disabled = false,
  error,
  id = "referenceImage",
  onBusyChange,
}: Props) {
  const input = useRef<HTMLInputElement>(null)
  const generation = useRef(0)
  const busyCallback = useRef(onBusyChange)
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState("")
  const displayedError = localError || error

  useEffect(() => {
    busyCallback.current = onBusyChange
  }, [onBusyChange])
  useEffect(
    () => () => {
      generation.current += 1
      busyCallback.current?.(false)
    },
    [],
  )

  async function selectImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file || disabled || busy) return
    const current = ++generation.current
    setLocalError("")
    setBusy(true)
    busyCallback.current?.(true)
    try {
      const prepared = await prepareBrickReferenceImage(file)
      if (generation.current === current) onChange(prepared)
    } catch (cause) {
      if (generation.current === current)
        setLocalError(
          cause instanceof Error
            ? cause.message
            : "画像を読み込めませんでした。別の画像を選んでください。",
        )
    } finally {
      if (generation.current === current) {
        setBusy(false)
        busyCallback.current?.(false)
        if (input.current) input.current.value = ""
      }
    }
  }

  function removeImage() {
    generation.current += 1
    setLocalError("")
    if (input.current) input.current.value = ""
    onChange(null)
  }

  return (
    <div className={styles.field} aria-busy={busy}>
      <label htmlFor={id} className={styles.label}>
        参考画像・サンプル（任意）
      </label>
      <p id={`${id}-hint`} className={styles.hint}>
        ロゴ・制作イメージ・参考にしたいサンプルの画像を1枚添付できます。JPEG・PNG・WebP、10MB以下。
      </p>
      {value && (
        <figure className={styles.preview}>
          <Image
            src={value.dataUrl}
            alt="選択した参考画像"
            width={value.width}
            height={value.height}
            unoptimized
          />
          <figcaption>{value.name}</figcaption>
        </figure>
      )}
      <div className={styles.actions}>
        <input
          ref={input}
          id={id}
          className={styles.input}
          type="file"
          tabIndex={-1}
          accept="image/jpeg,image/png,image/webp"
          disabled={disabled || busy}
          aria-describedby={`${id}-hint ${id}-privacy ${id}-status${displayedError ? ` ${id}-error` : ""}`}
          aria-invalid={displayedError ? true : undefined}
          onChange={selectImage}
        />
        <button
          type="button"
          className={styles.choose}
          disabled={disabled || busy}
          aria-describedby={`${id}-hint ${id}-privacy ${id}-status${displayedError ? ` ${id}-error` : ""}`}
          onClick={() => input.current?.click()}
        >
          {value ? "画像を変更" : "画像を選ぶ"}
        </button>
        {value && (
          <button
            type="button"
            className={styles.remove}
            disabled={disabled || busy}
            onClick={removeImage}
          >
            画像を削除
          </button>
        )}
      </div>
      <p className={styles.status} id={`${id}-status`} role="status">
        {busy
          ? "画像を準備しています…"
          : value
            ? "参考画像を選択済みです。変更する場合は画像を選び直してください。"
            : ""}
      </p>
      {displayedError && (
        <p id={`${id}-error`} className={styles.error} role="alert">
          {displayedError}
        </p>
      )}
      <p id={`${id}-privacy`} className={styles.note}>
        担当者が画像を確認し、お見積もりをご案内します。画像はお問い合わせの送信時に添付されます。
      </p>
    </div>
  )
}
